import clsx from "clsx";

interface SwitchProps {
  checked: boolean;
  onChange: (next: boolean) => void;
  /** Accessible label rendered visually-hidden when no `children` are provided. */
  ariaLabel?: string;
  disabled?: boolean;
  size?: "sm" | "md";
  className?: string;
}

const SIZES: Record<NonNullable<SwitchProps["size"]>, { track: string; knob: string; translate: string }> = {
  sm: {
    track: "h-5 w-9",
    knob: "h-4 w-4",
    translate: "translate-x-4",
  },
  md: {
    track: "h-6 w-11",
    knob: "h-5 w-5",
    translate: "translate-x-5",
  },
};

/**
 * Modern toggle switch built on a native `<button role="switch">` so screen readers
 * and keyboards work without extra config. Used for on/off state on cards (e.g. a
 * campaign being enabled) where a checkbox would feel less direct.
 */
export default function Switch({
  checked,
  onChange,
  ariaLabel,
  disabled,
  size = "md",
  className,
}: SwitchProps): JSX.Element {
  const s = SIZES[size];
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      aria-label={ariaLabel}
      disabled={disabled}
      onClick={() => onChange(!checked)}
      className={clsx(
        "relative inline-flex shrink-0 cursor-pointer items-center rounded-full transition focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-400 focus-visible:ring-offset-2 focus-visible:ring-offset-slate-950 disabled:cursor-not-allowed disabled:opacity-50",
        s.track,
        checked
          ? "bg-gradient-to-r from-brand-500 to-fuchsia-500 shadow-inner shadow-brand-900/30"
          : "bg-slate-700/80",
        className
      )}
    >
      <span
        className={clsx(
          "inline-block transform rounded-full bg-white shadow-md transition",
          s.knob,
          checked ? s.translate : "translate-x-0.5"
        )}
      />
    </button>
  );
}
